import { useAppDispatch, useAppSelector } from '@/app/hooks';
import type { RootState } from '@/app/store';
import { useWebinarT } from '@/hooks/useWebinarT';
import Title from '@/components/ui/Title';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import { useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  fetchPublicEvaluation,
  fetchPublicOffer,
  fetchPublicSession,
  fetchPublicThankYouPage,
  fetchPublicWorkspaceSocialMediaLinks,
} from '@/features/live/liveThunks';
import { socialMediaOptions } from '@/components/pages/webinars/resources/social-media/SocialMediaSection';
import EvaluationForm from '@/components/pages/thankyou/EvaluationForm';
import type { WorkspaceVisualIdentity } from '@/features/workspace/types';
import OfferThankYou from '@/components/pages/thankyou/OfferThankYou';
import { PoweredByLogo } from '@/components/pages/public/PoweredByLogo';
import SplashScreen from '@/components/common/SplashScreen';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';

export default function ThankYouPage() {
  const { t } = useWebinarT('webinars');
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const { webinarId: webinarIdParam, sessionIdentifier: sessionIdentifierParam } = useParams<{
    webinarId: string;
    sessionIdentifier: string;
  }>();

  const webinarId = webinarIdParam ? parseInt(webinarIdParam, 10) : null;
  const sessionIdentifier = sessionIdentifierParam;

  useEffect(() => {
    if (!webinarId || !sessionIdentifier) {
      return;
    }

    dispatch(
      fetchPublicSession({
        webinarId,
        sessionIdentifier,
      }),
    );
  }, [webinarId, sessionIdentifier, dispatch]);

  const publicSession = useAppSelector((state: RootState) => state.live.publicSession);
  const publicThankYouPage = useAppSelector((state: RootState) => state.live.publicThankYouPage);
  const publicSessionLoading = useAppSelector(
    (state: RootState) => state.live.publicSessionLoading,
  );
  const publicSessionError = useAppSelector((state: RootState) => state.live.publicSessionError);
  const publicThankYouPageLoading = useAppSelector(
    (state: RootState) => state.live.publicThankYouPageLoading,
  );
  const publicThankYouPageError = useAppSelector(
    (state: RootState) => state.live.publicThankYouPageError,
  );

  const visualIdentity = useAppSelector(
    (state: RootState) => state.live.publicSession?.visualIdentity,
  );

  const workspaceId = useAppSelector(
    (state: RootState) => state.live.publicSession?.session?.workspaceId,
  );

  useEffect(() => {
    if (!workspaceId) return;

    dispatch(
      fetchPublicWorkspaceSocialMediaLinks({
        workspaceId,
      }),
    );
  }, [workspaceId, dispatch]);

  useEffect(() => {
    if (!webinarId || !token) return;

    dispatch(
      fetchPublicThankYouPage({
        webinarId,
        authorization: token,
      }),
    );
  }, [webinarId, token, dispatch]);

  const criticalBackupUrl = publicSession?.session?.criticalBackupUrl;
  useEffect(() => {
    if (!criticalBackupUrl) return;
    globalThis.location.replace(criticalBackupUrl);
  }, [criticalBackupUrl]);

  useEffect(() => {
    if (!webinarId || !publicThankYouPage?.displayEvaluation || !token) return;

    dispatch(
      fetchPublicEvaluation({
        webinarId,
        authorization: token,
      }),
    );
  }, [webinarId, token, publicThankYouPage?.displayEvaluation, dispatch]);

  useEffect(() => {
    if (!webinarId || !publicThankYouPage?.displayOffer || !token) return;

    dispatch(
      fetchPublicOffer({
        webinarId,
        offerId: publicThankYouPage.offerId ?? 0,
        authorization: token,
      }),
    );
  }, [webinarId, token, publicThankYouPage?.displayOffer, dispatch]);

  const publicEvaluation = useAppSelector((state: RootState) => state.live.publicEvaluation);
  const publicOffer = useAppSelector((state: RootState) => state.live.publicOffer);

  const socialMediaLinks = useAppSelector(
    (state: RootState) => state.live.publicWorkspaceSocialMediaLinks,
  );

  if (!token) {
    return <div>{t('thankyouPage.tokenNotFound')}</div>;
  }

  const shouldFetchSession = Boolean(webinarId && sessionIdentifier);
  const sessionFetchFailed =
    shouldFetchSession && !publicSessionLoading && publicSessionError != null;
  const shouldFetchThankYou = Boolean(webinarId && token);
  const thankYouPageFetchFailed =
    shouldFetchThankYou && !publicThankYouPageLoading && publicThankYouPageError != null;

  if (publicSessionLoading || publicThankYouPageLoading) {
    return <SplashScreen label={t('thankyouPage.loading')} />;
  }
  if (sessionFetchFailed || thankYouPageFetchFailed) {
    return <SplashScreen label={t('thankyouPage.sessionNotFound')} fullscreen />;
  }

  const pageBg = visualIdentity?.backgroundColor || '#FBFBFB';

  const hasOffer = Boolean(publicThankYouPage?.displayOffer && publicOffer);
  const hasEvaluation = Boolean(
    visualIdentity &&
    publicThankYouPage?.displayEvaluation &&
    publicEvaluation &&
    webinarId &&
    publicSession?.session?.id &&
    token,
  );
  const useTwoColumnLayout = hasOffer && hasEvaluation;

  const offerBlock = hasOffer && (
    <OfferThankYou
      visualIdentity={visualIdentity as WorkspaceVisualIdentity}
      offer={publicOffer!}
      webinarId={webinarId ?? undefined}
      sessionIdentifier={sessionIdentifier ?? undefined}
      authorization={token ?? undefined}
    />
  );

  const evaluationBlock = hasEvaluation && (
    <>
      <p
        className={cn(
          visualIdentity?.theme === 'DARK' ? 'text-secondary-inverse' : 'text-secondary',
          'text-sm whitespace-pre-line',
          !useTwoColumnLayout && 'text-center',
        )}
      >
        {publicThankYouPage?.evaluationIntroduction?.trim() ||
          t('thankyouPage.defaultEvaluationIntro')}
      </p>
      <EvaluationForm
        visualIdentity={visualIdentity as WorkspaceVisualIdentity}
        evaluation={publicEvaluation!}
        webinarId={webinarId!}
        sessionId={publicSession!.session.id}
        authorization={token}
      />
    </>
  );

  const displaySpeakerProfile = publicThankYouPage?.displaySpeakerProfile ?? false;
  const displaySocialMedia = publicThankYouPage?.displaySocialMedia ?? false;

  const presenterCard = (displaySpeakerProfile || displaySocialMedia) && (
    <div
      className="w-full p-4 rounded-[20px] flex flex-col sm:flex-row items-center sm:items-stretch justify-center gap-4"
      style={{
        backgroundColor:
          visualIdentity?.theme === 'DARK' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(31, 31, 31, 0.03)',
      }}
    >
      {displaySpeakerProfile && (
        <div className={cn('flex flex-col gap-3', displaySocialMedia ? 'flex-1' : 'items-center')}>
          {publicSession?.session.presenters.map((presenter, index) => (
            <div key={presenter.fullname + index} className="flex items-center gap-2">
              <Avatar className="size-12 shrink-0">
                <AvatarImage src={presenter.pictureUrl} alt="presenter" />
                <AvatarFallback>
                  {(index === 0
                    ? (presenter.fullname ?? publicThankYouPage?.presenterName ?? '')
                    : (presenter.fullname ?? '')
                  )
                    .split(' ')
                    .filter(Boolean)
                    .map((w) => w[0].toUpperCase())
                    .slice(0, 2)
                    .join('')}
                </AvatarFallback>
              </Avatar>
              <div
                className={`flex flex-col gap-1 ${presenter.job === 'Host' ? 'items-center' : 'items-start'} justify-center`}
              >
                <p
                  className="text-xl font-medium"
                  style={{ color: visualIdentity?.theme === 'DARK' ? 'white' : 'black' }}
                >
                  {index === 0
                    ? (presenter.fullname ?? publicThankYouPage?.presenterName)
                    : presenter.fullname}
                </p>
                {presenter.job !== 'Host' && (
                  <p
                    className="text-base break-all"
                    style={{ color: visualIdentity?.theme === 'DARK' ? 'white' : 'black' }}
                  >
                    {presenter.job === 'Host' || presenter.job === 'Présentateur'
                      ? t('waitingRoom.publicPage.presenterLabel')
                      : presenter.job}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      {displaySpeakerProfile && displaySocialMedia && (
        <>
          <div className="items-stretch hidden sm:flex">
            <Separator orientation="vertical" className="bg-input w-px" />
          </div>
          <div className="items-stretch flex sm:hidden w-full">
            <Separator orientation="horizontal" className="bg-input w-full h-px" />
          </div>
        </>
      )}
      {displaySocialMedia && (
        <div className="space-y-1 flex-1 flex flex-col justify-center items-center gap-2">
          <p
            className="text-sm"
            style={{ color: visualIdentity?.theme === 'DARK' ? 'white' : 'black' }}
          >
            {t('thankyouPage.findMeOnSocial')}
          </p>
          {socialMediaLinks && socialMediaLinks.length > 0 ? (
            <div className="flex items-center gap-2 justify-center">
              {socialMediaLinks.map((link) => {
                const value = link.socialMedia.toUpperCase();
                const option = socialMediaOptions.find((opt) => opt.value === value);
                if (!option?.icon) return null;
                return (
                  <a
                    key={link.id}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center"
                    aria-label={link.socialMedia}
                  >
                    {option.icon}
                  </a>
                );
              })}
            </div>
          ) : (
            <div className="flex items-center gap-2 justify-center">
              {Array.from({ length: 3 }).map((_, index) => (
                <div key={index + 'empty-social-media'} className="w-6 h-6 bg-input rounded-full" />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );

  const titleBlock = (
    <div className={cn(!useTwoColumnLayout && 'text-center')}>
      <p
        className={cn(
          visualIdentity?.theme === 'DARK' ? 'text-secondary-inverse' : 'text-secondary',
          'text-secondary font-normal',
        )}
      >
        {publicThankYouPage?.subtitle?.trim() || t('thankyouPage.defaultSubtitle')}
      </p>
      <Title
        level={1}
        className={cn(visualIdentity?.theme === 'DARK' ? 'text-white' : 'text-black')}
      >
        {publicThankYouPage?.title?.trim() || t('thankyouPage.defaultTitle')}
      </Title>
    </div>
  );

  const poweredByLogo = (
    <PoweredByLogo
      theme={publicSession?.visualIdentity?.theme as 'LIGHT' | 'DARK'}
      fontFamily={publicSession?.visualIdentity?.fontFamily}
      isNotLivePage={true}
    />
  );

  return (
    <div
      className="w-full h-full min-h-svh relative flex items-center justify-center p-4 sm:p-12 lg:p-8"
      style={{ backgroundColor: pageBg }}
    >
      <div
        className={cn(
          'w-full h-full max-w-[1440px] mx-auto flex flex-col gap-12',
          useTwoColumnLayout
            ? 'lg:flex-row items-start lg:gap-41 justify-between'
            : 'items-center justify-center',
        )}
      >
        {useTwoColumnLayout ? (
          <>
            <div className="w-full flex flex-1 flex-col gap-8">
              {visualIdentity?.logoUrl && publicThankYouPage?.displayLogo && (
                <div className="mx-auto lg:mx-0">
                  <img
                    src={visualIdentity.logoUrl}
                    alt={t('thankyouPage.companyLogoAlt')}
                    className="w-20"
                  />
                </div>
              )}
              {hasOffer && <div className="lg:hidden">{offerBlock}</div>}
              {titleBlock}
              {evaluationBlock}
            </div>

            <div className="w-full flex flex-1 flex-col gap-6 lg:sticky top-8 items-center">
              <div className="w-full hidden lg:flex items-center justify-end">{poweredByLogo}</div>
              {hasOffer && <div className="hidden lg:block w-full">{offerBlock}</div>}
              {publicThankYouPage?.displayImage && publicThankYouPage.imageUrl && (
                <img
                  src={publicThankYouPage.imageUrl}
                  alt={t('thankyouPage.offerImageAlt')}
                  className="w-full h-full rounded-[20px]"
                />
              )}
              {presenterCard}
              <div className="w-full py-4 flex lg:hidden items-center justify-center">
                {poweredByLogo}
              </div>
            </div>
          </>
        ) : (
          <div className="w-full flex flex-col items-center gap-8 max-w-2xl">
            {visualIdentity?.logoUrl && publicThankYouPage?.displayLogo && (
              <img
                src={visualIdentity.logoUrl}
                alt={t('thankyouPage.companyLogoAlt')}
                className="w-20"
              />
            )}
            {titleBlock}
            {hasOffer && <div className="w-full">{offerBlock}</div>}
            {evaluationBlock}
            {publicThankYouPage?.displayImage && publicThankYouPage.imageUrl && (
              <img
                src={publicThankYouPage.imageUrl}
                alt={t('thankyouPage.offerImageAlt')}
                className="w-full rounded-[20px]"
              />
            )}
            <div className="w-full">{presenterCard}</div>
            <div className="w-full py-4 flex items-center justify-center">{poweredByLogo}</div>
          </div>
        )}
      </div>
    </div>
  );
}
